import { motion } from 'framer-motion';
import {
  ArrowLeft, Users, Music, Mic2, Video, Sparkles, Megaphone, Calendar,
  Award, Globe, Disc3, TrendingUp, Star, MapPin, Radio, Zap
} from 'lucide-react';

const ACHIEVEMENTS = [
  { icon: Disc3, value: '40+', label: 'Disques d\'or, platine & micros d\'argent', accent: 'yellow' },
  { icon: TrendingUp, value: '200M+', label: 'Visualisations sur les réseaux', accent: 'orange' },
  { icon: Award, value: '#1', label: 'BLACK BOX — projet le plus vendu en Italie', accent: 'red' },
  { icon: Users, value: '14+', label: 'Artistes développés au label', accent: 'yellow' },
  { icon: Globe, value: '4', label: 'Villes : Italie · NY · Paris · Guadeloupe', accent: 'orange' },
  { icon: Calendar, value: '30 ans', label: 'KAZAJAH STUDIO depuis 1995', accent: 'red' },
];

const CAREER = [
  { year: '1989', title: 'BLACK BOX', desc: 'DJHALLMANN co-producteur, chorégraphe et réalisateur du projet. "Ride on Time" #1 UK, meilleur single de 1989. Album Dreamland : 10M+ ventes, disque d\'or UK/USA, double platine Australie.' },
  { year: '1995', title: 'KAZAJAH STUDIO', desc: 'Fondation du studio à Milan puis expansion internationale. Base créative entre 4 continents.' },
  { year: '1990s', title: 'CORONA & BLACK MACHINE', desc: 'Participation à la réalisation des projets CORONA ("Try Me Out") et BLACK MACHINE — icônes de l\'Eurodance mondiale. 40+ disques d\'or, platine et micros d\'argent cumulés.' },
  { year: '2004', title: 'LABEL VERNABEL', desc: 'Création du label pour développer les artistes de la nouvelle génération : Pablo G, Regina Jhey, Annajah, MD No One, Tristreet, Moonjasz Project, Kareen…' },
  { year: '2020+', title: 'ELEKTRO KA · West Indies', desc: 'DJHALLMANN devient ambassadeur d\'ELEKTRO KA dans les West Indies — un nouveau fléau musical KARIBEAN qui fusionne Gwo Ka traditionnel et productions électroniques.' },
  { year: '2025', title: 'PAPYON ELECTRO', desc: 'Dernière sortie avec Laurent Succab. KAZAJAH STUDIO passe le cap de l\'IA créative : DAW propriétaire, génération musicale, sample library.' },
];

const SERVICES = [
  { icon: Users, title: 'Développement artistique', desc: 'Accompagnement complet d\'artistes émergents et confirmés — vision, direction artistique, coaching studio & scène.' },
  { icon: Music, title: 'Beats · Instrus · Jingles', desc: 'Création sur-mesure : rap, dancehall, électro, gwo ka, jingles publicitaires, thèmes radios.' },
  { icon: Mic2, title: 'Production Audio', desc: 'Prise de son, mixage, mastering. 30 ans d\'expertise gravés dans chaque piste.' },
  { icon: Video, title: 'Production Vidéo', desc: 'Clips musicaux, captation live, contenus réseaux. Réalisation cinéma-quality.' },
  { icon: Sparkles, title: 'Maîtrise de l\'IA créative', desc: 'DAW propriétaire avec IA, génération vocale, beat maker, sample library. Nous maîtrisons l\'IA comme des maîtres, pas comme des utilisateurs.' },
  { icon: Megaphone, title: 'Promotion', desc: 'Stratégie réseaux, PR, placements playlists, partenariats. Amplification internationale.' },
  { icon: Calendar, title: 'Organisation d\'événements', desc: 'SOUL TRAIN · ANCESTRAL PARTY · ADN PARTY · GUADELOUPE ELECTRO GROOVE — événements qui font vibrer les scènes.' },
];

const CITIES = [
  { flag: '🇮🇹', city: 'Italie (Milan)', role: 'Berceau historique du studio' },
  { flag: '🇺🇸', city: 'New York', role: 'Antenne créative & networking' },
  { flag: '🇫🇷', city: 'Paris', role: 'Studio & production' },
  { flag: '🇬🇵', city: 'Guadeloupe', role: 'Racines KARIBEAN — Elektro Ka' },
];

export default function AboutPage({ onBack }) {
  return (
    <div className="min-h-screen bg-black text-white pb-24 pt-28 md:pt-32" data-testid="about-page">
      {/* Back button */}
      <div className="px-4 md:px-8 lg:px-16 pt-4">
        <button onClick={onBack} data-testid="about-back-button"
          className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2 text-sm">
          <ArrowLeft size={16} /> Retour au site
        </button>
      </div>

      {/* ─────────── HERO ─────────── */}
      <section className="px-4 md:px-8 lg:px-16 pt-4 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -left-20 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="text-yellow-400 uppercase tracking-widest text-xs font-black mb-3">
            À propos · Depuis 1995
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-black leading-none mb-6">
            KAZAJAH STUDIO<br />
            <span className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
              LABEL VERNABEL
            </span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="text-lg md:text-xl text-zinc-300 max-w-3xl leading-relaxed">
            30 ans d&apos;expérience internationale. <span className="text-yellow-400 font-semibold">DJHALLMANN</span> — co-producteur, chorégraphe et réalisateur du projet
            <span className="text-yellow-400 font-semibold"> BLACK BOX</span>, le produit musical le plus vendu en Italie.
            Il participe à la réalisation de projets comme <span className="text-orange-400 font-semibold">CORONA</span> et
            <span className="text-orange-400 font-semibold"> BLACK MACHINE</span> — cumulant plus de <span className="text-yellow-400 font-bold">40 disques d&apos;or, platine et micros d&apos;argent</span>,
            et <span className="text-yellow-400 font-bold">200 millions de vues</span> sur les réseaux.
          </motion.p>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="mt-4 text-base md:text-lg text-zinc-400 max-w-3xl leading-relaxed">
            Ambassadeur d&apos;<span className="text-orange-400 font-semibold">ELEKTRO KA</span> dans les <span className="text-orange-400 font-semibold">West Indies</span> —
            un nouveau fléau musical <span className="text-yellow-400 font-bold">KARIBEAN</span> qui fusionne Gwo Ka traditionnel et productions électroniques.
          </motion.p>
        </div>
      </section>

      {/* ─────────── CHIFFRES CLÉS ─────────── */}
      <section className="px-4 md:px-8 lg:px-16 py-16 border-y border-zinc-900 bg-zinc-950/40">
        <div className="max-w-6xl mx-auto">
          <p className="text-yellow-400 uppercase tracking-widest text-xs font-black mb-6">Chiffres clés</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {ACHIEVEMENTS.map((a, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                data-testid={`about-achievement-${i}`}
                className={`p-6 rounded-2xl border bg-gradient-to-br from-black/40 to-zinc-900/40 hover:scale-[1.02] transition-transform ${
                  a.accent === 'yellow' ? 'border-yellow-500/30' :
                  a.accent === 'orange' ? 'border-orange-500/30' : 'border-red-500/30'
                }`}>
                <a.icon size={22} className={
                  a.accent === 'yellow' ? 'text-yellow-400 mb-3' :
                  a.accent === 'orange' ? 'text-orange-400 mb-3' : 'text-red-400 mb-3'
                } />
                <p className={`text-3xl md:text-4xl font-black mb-1 ${
                  a.accent === 'yellow' ? 'text-yellow-400' :
                  a.accent === 'orange' ? 'text-orange-400' : 'text-red-400'
                }`}>{a.value}</p>
                <p className="text-xs md:text-sm text-zinc-400 leading-snug">{a.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── CARRIÈRE / TIMELINE ─────────── */}
      <section className="px-4 md:px-8 lg:px-16 py-20">
        <div className="max-w-4xl mx-auto">
          <p className="text-yellow-400 uppercase tracking-widest text-xs font-black mb-3">Parcours</p>
          <h2 className="text-3xl md:text-4xl font-black mb-10">De BLACK BOX à ELEKTRO KA</h2>
          <div className="relative pl-8 md:pl-12">
            <div className="absolute left-2 md:left-4 top-2 bottom-2 w-0.5 bg-gradient-to-b from-yellow-500 via-orange-500 to-red-500" />
            {CAREER.map((c, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                data-testid={`about-career-${i}`}
                className="relative mb-10 last:mb-0">
                <div className="absolute -left-8 md:-left-12 top-1 w-4 h-4 rounded-full bg-yellow-500 ring-4 ring-yellow-500/30" />
                <p className="text-yellow-400 text-xs font-black tracking-widest mb-1">{c.year}</p>
                <h3 className="text-xl md:text-2xl font-black text-white mb-2">{c.title}</h3>
                <p className="text-zinc-400 text-sm md:text-base leading-relaxed">{c.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── SERVICES ─────────── */}
      <section className="px-4 md:px-8 lg:px-16 py-20 border-y border-zinc-900 bg-zinc-950/40">
        <div className="max-w-6xl mx-auto">
          <p className="text-yellow-400 uppercase tracking-widest text-xs font-black mb-3">Savoir-faire</p>
          <h2 className="text-3xl md:text-4xl font-black mb-2">Tout sous un même toit</h2>
          <p className="text-zinc-500 text-sm md:text-base mb-10 max-w-2xl">De l&apos;idée à la scène — 7 services complémentaires pour transformer chaque projet artistique.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map((s, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                data-testid={`about-service-${i}`}
                className="p-5 rounded-2xl border border-zinc-800 hover:border-yellow-500/40 bg-zinc-900/40 hover:bg-zinc-900/70 transition-all">
                <div className="w-10 h-10 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center mb-3">
                  <s.icon size={18} className="text-yellow-400" />
                </div>
                <h3 className="text-white font-black text-lg mb-2">{s.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── VILLES ─────────── */}
      <section className="px-4 md:px-8 lg:px-16 py-20">
        <div className="max-w-5xl mx-auto">
          <p className="text-yellow-400 uppercase tracking-widest text-xs font-black mb-3">Présence internationale</p>
          <h2 className="text-3xl md:text-4xl font-black mb-10">4 villes · 4 continents créatifs</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CITIES.map((c, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                data-testid={`about-city-${i}`}
                className="p-6 rounded-2xl border border-yellow-500/20 bg-gradient-to-br from-yellow-500/5 to-transparent hover:border-yellow-500/50 transition-all text-center">
                <p className="text-4xl mb-3">{c.flag}</p>
                <p className="text-white font-black text-lg mb-1">{c.city}</p>
                <p className="text-zinc-500 text-xs">{c.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── CTA ─────────── */}
      <section className="px-4 md:px-8 lg:px-16 py-20 border-t border-zinc-900">
        <div className="max-w-3xl mx-auto text-center">
          <Star size={32} className="text-yellow-400 mx-auto mb-4" />
          <h2 className="text-3xl md:text-4xl font-black mb-4">Prêt à passer au niveau ?</h2>
          <p className="text-zinc-400 text-base md:text-lg mb-8 leading-relaxed">
            Que tu sois artiste émergent, DJ confirmé, marque cherchant un jingle, ou label à la recherche d&apos;un partenariat pro — nous t&apos;accueillons.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="#contact"
              onClick={(e) => { e.preventDefault(); onBack(); setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 200); }}
              data-testid="about-cta-contact"
              className="px-6 py-3 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-black hover:scale-105 transition-transform shadow-lg shadow-yellow-500/30 flex items-center gap-2">
              <Zap size={16} /> Nous contacter
            </a>
            <a href="/community"
              data-testid="about-cta-community"
              className="px-6 py-3 rounded-full border border-yellow-500/40 text-yellow-300 font-bold hover:bg-yellow-500/10 transition-colors flex items-center gap-2">
              <Radio size={16} /> Soumettre un projet
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
